// Derived rollups over a ClaimProjection's months. These values are NOT
// stored on the projection — recomputed on demand from the month stream so
// the in-memory shape stays minimal and identities can't drift.
//
// All helpers are pure. UI code that needs a running cumulative typically
// wants the `*Series` variants (one value per month, same length as
// `months`); single-number aggregates use `total*` / `final*`.

export function getLtcPool(state: MonthlyState): number {
  return state.pools.baseBenefitPool + state.pools.extensionOfBenefitsPool;
}

function getEpLumpSumPaid(month: MonthlyEntry): number {
  const { strategyDeltas } = month.deltas;
  return typeof (strategyDeltas as { epLumpSumPaid?: number })?.epLumpSumPaid === 'number'
    ? (strategyDeltas as { epLumpSumPaid: number }).epLumpSumPaid
    : 0;
}

// Dollars paid by the policy in a single month (regular indemnity coverage
// plus any retroactive lump in strategyDeltas). Matches the predicate
// previously used to derive `firstPayoutRowIndex`.
export function getMonthPayout(month: MonthlyEntry): number {
  const { payout } = month.deltas;
  // `ltcCoveragePaid` is cost-covered dollars and can overlap with
  // `indemnityPayout`; monthly cash payout should count only one base payout
  // stream plus any EP lump.
  const basePayout = Math.max(payout.indemnityPayout, payout.ltcCoveragePaid);
  return basePayout + getEpLumpSumPaid(month);
}

export function getFirstPayoutMonthIndex(months: MonthlyEntry[]): number | null {
  for (let i = 0; i < months.length; i += 1) {
    if (getMonthPayout(months[i]) > 0) return i;
  }
  return null;
}

// Per-month cumulative LTC payout (running sum of indemnity + cost-coverage
// + retroactive lump). Final element is the lifetime total.
export function getCumulativeLtcPayoutSeries(months: MonthlyEntry[]): number[] {
  const out = new Array<number>(months.length);
  let running = 0;
  for (let i = 0; i < months.length; i += 1) {
    running += getMonthPayout(months[i]);
    out[i] = running;
  }
  return out;
}

export function getTotalLtcPayout(months: MonthlyEntry[]): number {
  let total = 0;
  for (const m of months) total += getMonthPayout(m);
  return total;
}

// Per-month cumulative out-of-pocket cost (running sum of payout.ineligibleCost).
export function getCumulativeIneligibleCostSeries(months: MonthlyEntry[]): number[] {
  const out = new Array<number>(months.length);
  let running = 0;
  for (let i = 0; i < months.length; i += 1) {
    running += months[i].deltas.payout.ineligibleCost;
    out[i] = running;
  }
  return out;
}

export function getTotalIneligibleCost(months: MonthlyEntry[]): number {
  let total = 0;
  for (const m of months) total += m.deltas.payout.ineligibleCost;
  return total;
}

// ──────────────────────────────────────────────────────────────────────────
// LTC vs Non-LTC split rollups — match the funding-table taxonomy:
//   LTC payout      = cost-covering payouts (ltcCoveragePaid + EP lump)
//   Non-LTC payout  = indemnity surplus (paid in excess of cost) +
//                     death benefit remaining at horizon
//
// Sum of getTotalLtcCoveragePayout + getTotalNonLtcPayout = getTotalPayout.
// ──────────────────────────────────────────────────────────────────────────

// Sum of cost-covering payouts: `ltcCoveragePaid` + any retroactive EP lump.
// Excludes indemnity surplus (which is classified as non-LTC because it is
// paid in excess of incurred care cost).
export function getTotalLtcCoveragePayout(months: MonthlyEntry[]): number {
  let total = 0;
  for (const m of months) total += m.deltas.payout.ltcCoveragePaid + getEpLumpSumPaid(m);
  return total;
}

export function getTotalIndemnityPayout(months: MonthlyEntry[]): number {
  let total = 0;
  for (const m of months) total += m.deltas.payout.indemnityPayout;
  return total;
}

// Indemnity dollars that exceed this row's direct cost-coverage amount.
// This is the non-LTC cash component attributable to indemnity.
export function getTotalIndemnitySurplus(months: MonthlyEntry[]): number {
  let total = 0;
  for (const m of months) {
    const { indemnityPayout, ltcCoveragePaid } = m.deltas.payout;
    total += Math.max(indemnityPayout - ltcCoveragePaid, 0);
  }
  return total;
}

// Death benefit pool remaining at the end of the projection. Paid to
// beneficiaries on death; counts as a non-LTC return for ROI purposes.
export function getRemainingDeathBenefit(months: MonthlyEntry[]): number {
  if (months.length === 0) return 0;
  return months[months.length - 1].state.pools.deathBenefit;
}

export function getTotalNonLtcPayout(months: MonthlyEntry[]): number {
  return getTotalIndemnitySurplus(months) + getRemainingDeathBenefit(months);
}

// LTC coverage + non-LTC payout (indemnity surplus + remaining DB).
// This is the "total returns" figure that would feed an ROI calculation.
export function getTotalPayout(months: MonthlyEntry[]): number {
  return getTotalLtcCoveragePayout(months) + getTotalNonLtcPayout(months);
}

// Total inflation-adjusted care cost incurred across the projection,
// regardless of who paid. Equals policy LTC coverage + ineligible cost
// (modulo retroactive lump offsets).
export function getTotalProjectedCareCost(months: MonthlyEntry[]): number {
  let total = 0;
  for (const m of months) total += m.facts.effectiveCareCost;
  return total;
}
